import { color, font } from '@central/ui';
import { Checkbox, UnstyledButton } from '@mantine/core';
import type { FormEvent, ReactNode } from 'react';
import { Brand } from '../shell/Brand';

/** The page around every sign-in panel: brand above the panel. */
export function SignInPage({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: color.signInPage,
        color: color.signInText,
        fontFamily: font.sans,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 32,
        padding: '48px 16px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Brand />
      </div>
      {children}
    </div>
  );
}

export function Panel({
  step,
  of,
  title,
  onSubmit,
  children,
}: {
  step: number;
  of: number;
  title: string;
  onSubmit?: (e: FormEvent) => void;
  children: ReactNode;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.(e);
      }}
      noValidate
      style={{
        width: '100%',
        maxWidth: 420,
        background: color.signInPanel,
        border: `1px solid ${color.signInPanelBorder}`,
        borderRadius: 14,
        padding: 32,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: color.consoleFooterText }}>
          STEP {step} OF {of}
        </span>
        <h1 style={{ margin: 0, fontSize: 24, lineHeight: '32px', color: color.signInHeading }}>
          {title}
        </h1>
      </div>
      {children}
    </form>
  );
}

export function ErrorText({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: color.signInError }}
    >
      {children}
    </p>
  );
}

export function LinkButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <UnstyledButton
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{ color: color.signInLink, fontSize: 13, minHeight: 32, textDecoration: 'underline' }}
    >
      {children}
    </UnstyledButton>
  );
}

export function LinkRow({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

/** "Trust this browser for 7 days": sent as trustBrowser on the step that completes sign-in. */
export function TrustBrowser({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Checkbox
      label="Trust this browser for 7 days"
      checked={checked}
      onChange={(e) => onChange(e.currentTarget.checked)}
      size="md"
      styles={{
        label: { color: color.signInLabel, fontSize: 14 },
        body: { alignItems: 'center', minHeight: 44 },
      }}
    />
  );
}
